import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/** How-to tab. Same convention as Commissions and Deal Health: the formulas
 *  and the judgement calls are documented in the app, not just in the code. */
export function HowToView({ isAdmin }: { isAdmin: boolean }) {
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <Section title="1. What this replaces">
        <p>
          The <em>Major Swag Items Usage</em> spreadsheet — one row per physical
          unit, including 17 rows that just said “Available”. Same information,
          three changes:
        </p>
        <ul>
          <li>
            <strong>Stock is arithmetic.</strong> A purchase records how many
            arrived; on-hand is that minus what has gone out. There is no
            “Available” column to forget to update.
          </li>
          <li>
            <strong>A shipment is a box, not a gift.</strong> The sheet put one
            $63.47 FedEx charge on the first of four blankets going to one NAPA
            address and left the other three blank. Here that is one shipment
            over four giveaways, and the freight splits four ways.
          </li>
          <li>
            <strong>Requests are recorded.</strong> Asking, approving, and
            handing over are separate events, so “who said yes to this” has an
            answer.
          </li>
        </ul>
      </Section>

      <Section title="2. Asking for swag">
        <p>
          Detail tab → <strong>Request swag</strong>. One line per recipient,
          even for four identical blankets — the duplicate-gift check works on
          names, so a request for “quantity 4” gives it nothing to check.
        </p>
        <p>
          As you type a recipient, the form checks the whole history and warns
          you at two levels: <strong>this person has had one before</strong>{" "}
          (red if it was the same item) and <strong>others at this company
          have</strong> (amber — usually fine, but worth seeing). Nothing is
          blocked; you decide.
        </p>
        <p>
          A submitted request <em>holds</em> the stock. Two people cannot both be
          promised the last blanket.
        </p>
      </Section>

      <Section title="3. What approval does">
        <p>
          An admin approves, and each request line becomes a ledger row drawn
          from a real purchase order at that order’s price. Approval is the point
          where stock actually moves and cost is fixed — re-pricing next year’s
          order can never restate a month that has been posted.
        </p>
        <p>
          Stock is drawn oldest-order-first, except that an order earmarked to
          the spending department is used before an older unearmarked one.
          Otherwise Marketing’s reserved sets would sit untouched while their own
          requests drained the general pool.
        </p>
      </Section>

      <Section title="4. Shipping">
        <p>
          If a request needs mailing, Adrey records the carrier, tracking number,
          cost, and ship date. The Summary tab shows everything in flight plus
          anything delivered in the last 30 days, then it drops off — the ledger
          keeps it forever, the summary stays short.
        </p>
        <p>
          <strong>Automatic status.</strong> Statuses can always be set by hand.
          When FedEx and USPS API credentials are configured, a job at 6am
          Mountain checks every shipment that hasn’t reached a final state and
          updates it. It will never walk a shipment backwards: if you marked a
          box delivered because the customer emailed a thank-you, a carrier feed
          that still says “in transit” does not override you. A problem status
          always wins, because that is worth surfacing.
        </p>
        <p className="text-muted-foreground">
          Scraping fedex.com with a browser was considered and rejected — both
          carriers use bot detection and serve CAPTCHAs on repeat lookups. The
          registered API route is free; it just needs an account.
        </p>
      </Section>

      {isAdmin && (
        <Section title="5. The Accounting roll-up">
          <p>Two numbers per department per month:</p>
          <ul>
            <li>
              <strong>Swag cost</strong> = units given × the unit price of the
              order they came from.
            </li>
            <li>
              <strong>Shipping cost</strong> = each giveaway’s pro-rata share of
              its box’s freight, by quantity.
            </li>
          </ul>
          <p>
            The month is the <strong>use date</strong> — when the swag was spent,
            not when it was bought and not when it shipped. Buying 20 pickleball
            sets in August is inventory; giving one away in October is October’s
            expense. Shipping follows the gift it belongs to, so a box that went
            out in November for an October giveaway lands in October. One
            giveaway, one month, one journal entry.
          </p>
          <p>
            Rows with no department show as <strong>Unassigned</strong> and are
            called out per month — they can’t be posted until someone says whose
            budget they came from. Departments are the active QuickBooks classes,
            so the class column pastes straight across.
          </p>
        </Section>
      )}

      {isAdmin && (
        <Section title="6. Earmarking stock">
          <p>
            Admin → Purchases → <strong>Reserve</strong> sets part of an order
            aside for a department. A reservation is a claim on stock, not a
            giveaway: it never hits the accounting roll-up, it just stops other
            departments spending those units. Reserved units drop out of the
            “free” count everyone else sees.
          </p>
        </Section>
      )}

      <Section title={isAdmin ? "7. Rows flagged for review" : "5. Rows flagged for review"}>
        <p>
          History imported from the spreadsheet exactly as written, typos
          included. Where a row was missing a recipient, missing a company, or
          named a department we couldn’t match, it is flagged rather than
          guessed at — filter to them with{" "}
          <strong>Needs review</strong> on the Detail tab. Cleaning someone
          else’s records by inference is how history quietly becomes fiction.
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 pb-4 text-sm leading-relaxed [&_li]:ml-4 [&_li]:list-disc [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-1">
        {children}
      </CardContent>
    </Card>
  );
}
